import { AppBar, Box, Button, Checkbox, Divider, Select, Stack, Typography } from '@mui/material';
import AtIcon from 'components/SvgIcons/AtIcon';
import ExportIcon from 'components/SvgIcons/ExportIcon';
import PrinterIcon from 'components/SvgIcons/PrinterIcon';
import { MenuItem } from 'components/styled/SelectMenuItem';
import { FrontDeskHeaderModel } from 'models/FrontDesk/ArrivalModel';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import ArrowDownIcon from '../../../../../components/SvgIcons/ArrowDownIcon';

export enum TriOptionsModel {
  // time = 'time',
  first_name = 'first_name',
  last_name = 'last_name',
  agency = 'agency',
  arrival_time = 'arrival_time',
  confirmation_number = 'confirmation_number',
  room_number = 'room_number',
  departure = 'departure',
  departure_time = 'departure_time',
  crs_number = 'crs_number',
  departure_date = 'departure',
  arrival_date = 'arrival',
  room_type = 'room_type',
}

export type SortDirection = 'asc' | 'desc';

type Props = {
  selectedCount: number;
  rowsCount: number;
  handleSelectAllClick?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClickPrint?: () => void;
  onClickSend?: () => void;
  onClickExport?: () => void;
  header: FrontDeskHeaderModel;
  sortBy: TriOptionsModel;
  setSortBy: (value: TriOptionsModel) => void;
  sortDirection: SortDirection;
  setSortDirection: (value: SortDirection) => void;
  toolbarFor: 'arrivals' | 'departures' | 'inHouses';
};

const FrontDeskToolbar: FC<Props> = ({
  selectedCount,
  rowsCount,
  handleSelectAllClick,
  onClickPrint,
  onClickSend,
  onClickExport,
  header,
  sortBy,
  setSortBy,
  sortDirection,
  setSortDirection,
  toolbarFor,
}) => {
  const { t } = useTranslation('frontDesk');
  const description = [
    t(toolbarFor, {
      count: Number(header?.adults) + Number(header?.children) || 0,
    }),
    t('totalRooms', { count: Number(header?.reservations ?? '-') }),
    t('adult', { count: Number(header?.adults) }),
    t('child', { count: Number(header?.children) }),
    t('babies', { count: Number(header?.infants) }),
    ...(toolbarFor === 'arrivals'
      ? [
          t('totalConfirmed', { count: Number(header?.confirmed) }),
          t('waitList', { count: Number(header?.wait_list) }),
        ]
      : []),
  ];

  const MenuItems = [
    // {
    //   value: TriOptionsModel.time,
    //   label: TriOptionsModel.time,
    // },
    { value: TriOptionsModel.first_name, label: TriOptionsModel.first_name },
    { value: TriOptionsModel.last_name, label: TriOptionsModel.last_name },
    { value: TriOptionsModel.agency, label: TriOptionsModel.agency },
    { value: TriOptionsModel.crs_number, label: TriOptionsModel.crs_number },
    {
      value: TriOptionsModel.confirmation_number,
      label: TriOptionsModel.confirmation_number,
    },
    { value: TriOptionsModel.room_number, label: TriOptionsModel.room_number },
    { value: TriOptionsModel.room_type, label: TriOptionsModel.room_type },
    { value: TriOptionsModel.arrival_date, label: TriOptionsModel.arrival_date },
    { value: TriOptionsModel.departure_date, label: TriOptionsModel.departure_date },

    ...(toolbarFor === 'arrivals'
      ? [
          {
            value: TriOptionsModel.arrival_time,
            label: TriOptionsModel.arrival_time,
          },
        ]
      : []),
    ...(toolbarFor === 'inHouses'
      ? [
          {
            value: TriOptionsModel.arrival_time,
            label: TriOptionsModel.arrival_time,
          },
          {
            value: TriOptionsModel.departure_time,
            label: TriOptionsModel.departure_time,
          },
        ]
      : []),
    ...(toolbarFor === 'departures'
      ? [
          {
            value: TriOptionsModel.departure_time,
            label: TriOptionsModel.departure_time,
          },
        ]
      : []),
  ];

  // Handle sort option and direction change
  const handleSortChange = (option: TriOptionsModel) => {
    setSortBy(option);
  };

  return (
    <AppBar
      sx={{
        position: 'sticky',
        top: 'auto',
        bgcolor: 'transparent',
        display: 'flex',
        height: '5rem',
        flex: '0 1 3.5rem',
        p: '1.5rem 1.5rem 1rem 1.5rem',
        zIndex: 0,
      }}
      elevation={0}
    >
      <Stack
        direction="row"
        flexGrow={1}
        bgcolor="colors.purple.o10"
        px="1rem"
        height="3.5rem"
        alignItems="center"
        justifyContent="space-between"
      >
        {toolbarFor !== 'inHouses' ? (
          <Checkbox
            sx={{ mr: '1rem' }}
            indeterminate={selectedCount > 0 && selectedCount < rowsCount}
            checked={rowsCount > 0 && selectedCount === rowsCount}
            onChange={handleSelectAllClick}
            inputProps={{
              'aria-label': 'select all desserts',
            }}
          />
        ) : null}

        <Typography
          component={Box}
          flexGrow={1}
          variant="inter14"
          color="colors.purple.o70"
          fontWeight={500}
        >
          {description.join(' . ')}
        </Typography>
        <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing="0.5rem">
          <Box>
            <Select
              size="small"
              sx={{
                '&.MuiInputBase-root': {
                  minWidth: '13rem',
                  minHeight: '1.5rem',
                  width: '12rem',
                  border: 'none',
                  boxShadow: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: 'colors.purple.o70',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    border: 'none',
                    boxShadow: 'none',
                  },
                  '&:focus': {
                    backgroundColor: 'transparent',
                    border: 'none',
                    boxShadow: 'none',
                  },
                },
              }}
              value={sortBy}
              onChange={e => handleSortChange(e.target.value as TriOptionsModel)}
              renderValue={(value: TriOptionsModel) => (
                <Typography
                  variant="inter14"
                  fontWeight={500}
                  sx={{
                    width: '13rem',
                    pr: 3,
                  }}
                >
                  Tri: Per {t(`tri.${value}`)}
                </Typography>
              )}
              MenuProps={{
                PaperProps: {
                  onClick: e => {
                    e.stopPropagation();
                  },
                },
              }}
            >
              {MenuItems?.map((item, index) => {
                return (
                  <MenuItem
                    value={item?.value}
                    key={index}
                    onClick={() => handleSortChange(item.value)}
                    sx={{
                      width: ' 13rem',
                    }}
                  >
                    {t(`tri.${item.label}`)} {sortBy === item.value}
                  </MenuItem>
                );
              })}
            </Select>
            <Button onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}>
              <Typography variant="inter14" color="colors.purple.o70">
                {sortDirection === 'asc' ? 'ASC' : 'DESC'}
              </Typography>
              <ArrowDownIcon
                fontSize="small"
                sx={{
                  ml: 1,
                  ...(sortDirection === 'asc' ? { transform: 'rotate(180deg)' } : {}),
                }}
              />
            </Button>
          </Box>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ borderWidth: 0.5, borderColor: 'colors.gray.o20' }}
          />

          <Button variant="text" sx={{ width: '2.5rem', height: '2.5rem' }} onClick={onClickPrint}>
            <PrinterIcon sx={{ color: 'colors.purple.o70' }} />
          </Button>
          <Button variant="text" sx={{ width: '2.5rem', height: '2.5rem' }} onClick={onClickSend}>
            <AtIcon sx={{ color: 'colors.purple.o70' }} />
          </Button>
          <Button variant="text" sx={{ width: '2.5rem', height: '2.5rem' }} onClick={onClickExport}>
            <ExportIcon sx={{ color: 'colors.purple.o70' }} />
          </Button>
        </Stack>
      </Stack>
    </AppBar>
  );
};

export default FrontDeskToolbar;
